import React, { useState, useEffect } from 'react';
import Moment from 'react-moment';
import axios from "axios";
import toastr from "toastr";
import { CustomLoading } from 'components/CustomLoading';
import { Modal, Button } from 'react-bootstrap';
import { Building2, Users, StickyNote, Mail, Shield, CreditCard, FileText, Settings } from "lucide-react";

const FundingEmailFrequency = [
  { value: 1, label: "1x per week (Monday)" },
  { value: 3, label: "3x per week (Mon, Wed, Fri) - Default" },
  { value: 5, label: "5x per week (Mon–Fri daily)" },
];

const ViewPolicy = ({ data }) => {
    const [hasHsa, setHasHsa] = useState(false);
    const [hasLsa, setHasLsa] = useState(false);
    const [hasFlex, setHasFlex] = useState(false);
    const [hasAdditionalAccounts, setHasAdditionalAccounts] = useState(false);
    const [policyNotes, setPolicyNotes] = useState('');
    const [policyDetails, setPolicyDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hasAccrual, setHasAccrual] = useState(false);
    const [syncingEa, setSyncingEa] = useState(false);
    const [showSyncConfirm, setShowSyncConfirm] = useState(false);

    useEffect(() => {
        setLoading(true);
        setHasAdditionalAccounts(data.hasAdditionalAccounts);
        if (data.id) {
			Promise.all([fetchPolicyDetails(), fetchPolicyNotes()]).finally(() => {
				setLoading(false);
			});
		}
    }, [data]);

    const handleNotesChange = (e) => {
        setPolicyNotes(e.target.value);
    };

    const fetchPolicyNotes = async () => {
        try {
          const response = await axios.get(`/api/support/policynotes?policyHolderId=${data.policyHolderId}`);
          if (response.data && response.data.noteText != null) {
            setPolicyNotes(response.data.noteText);
          } else {
            setPolicyNotes("");
          }
          setLoading(false);
        } catch (error) {
          toastr.error("Failed to fetch company notes", "Error");
        }
      };

      const fetchPolicyDetails = async () => {
        let localHasHsa = false;
        let localHasLsa = false;
        let localHasFlex = false;
        let localHasAccrual = false;

        try {
          const response = await axios.get(`/api/support/policydetails?policyId=${data.id}`);
          if (response.data) {

            setPolicyDetails({ ...response.data });
            response.data.categories.forEach(item => {
                if (item.hasHsa) {
                    localHasHsa = true;
                }
                if (item.hasLsa) {
                    localHasLsa = true;
                }
                if (item.hasFlex) {
                    localHasFlex = true;
                }
                if(item.hasAccrual){
                    localHasAccrual = true;
                }
            });
            setHasHsa(localHasHsa);
            setHasLsa(localHasLsa);
            setHasFlex(localHasFlex);
            setHasAccrual(localHasAccrual);
          } else {
            setPolicyDetails("");
          }
          setLoading(false);
        } catch (error) {
          toastr.error("Failed to fetch company notes", "Error");
        }
      };


    const savePolicyNotes = () => {
        var noteId = null;
        if (data.notes != null) {
            noteId = data.notes.id;
        }

        var body = {
            Id: noteId,
            PolicyHolderId: data.policyHolderId,
            Notes: policyNotes,
        };
        var self = this;
        axios({
            method: "post",
            url: "/api/support/savepolicynotes",
            data: body,
        })
        .then(function (response) {
            if (!response.data.success) {
                toastr.success("Notes updated", "Success");
            }
        })
        .catch(function (error) {
            toastr.error("Failed save notes", "Error");
        });
    };

    const confirmSyncEa = () => {
        setShowSyncConfirm(false);
        setSyncingEa(true);
        axios
            .post("/api/support/syncea", { policyId: data.id })
            .then(function (response) {
                if (response.data && response.data.success) {
                    if (!response.data.companyInserted && !response.data.policyInserted) {
                        toastr.info("Policy already synced - no changes", "EA API");
                    } else {
                        toastr.success("Policy synced to EA API", "Success");
                    }
                } else {
                    toastr.error(
                        (response.data && response.data.message) || "Failed to sync policy",
                        "Error"
                    );
                }
            })
            .catch(function (error) {
                const message =
                    error && error.response && error.response.data && error.response.data.message
                        ? error.response.data.message
                        : "Failed to sync policy";
                toastr.error(message, "Error");
            })
            .finally(function () {
                setSyncingEa(false);
            });
    };

    const renderPlanAdmins = () => {
        if (!policyDetails || !Array.isArray(policyDetails.planAdmins)) return null;
		return policyDetails.planAdmins && policyDetails.planAdmins.length > 0 ? (
			policyDetails.planAdmins.map((item, i) => (
				<div key={i}>
					{item.lastName} {item.firstName} <a href={`mailto:${item.email}`} target="_blank" rel="noopener noreferrer" style={{ color: "blue" }}>{item.email}</a>
					{item.readOnly && <span style={{ marginLeft: "6px", color: "#856404", backgroundColor: "#fff3cd", border: "1px solid #ffc107", borderRadius: "3px", padding: "1px 6px", fontSize: "12px", fontWeight: 500 }}>Read Only</span>}
					{!item.isActive && <span style={{ marginLeft: "6px", color: "#721c24", backgroundColor: "#f8d7da", border: "1px solid #f5c6cb", borderRadius: "3px", padding: "1px 6px", fontSize: "12px", fontWeight: 500 }}>Inactive</span>}
				</div>
			))
		) : null;
	};

    const notes = data.notes ? (
        <div>
            <div>
                <span>
                    <Moment local format="YYYY/MM/DD hh:mm A">
                        {data.notes.updatedAt}
                    </Moment>
                </span>
            </div>
            <div className="mt-2">
                <textarea
                    className="form-control"
                    value={policyNotes}
                    onChange={handleNotesChange}
                ></textarea>
                <button
                    type="button"
                    className="btn btn-primary btn-sm mt-1"
                    onClick={savePolicyNotes}
                >
                    Save
                </button>
            </div>
        </div>
    ) : (
        <div className="mt-2">
            <textarea
                className="form-control"
                value={policyNotes}
                onChange={handleNotesChange}
            ></textarea>
            <button
                type="button"
                className="btn btn-primary btn-sm mt-1"
                onClick={savePolicyNotes}
            >
                Save
            </button>
        </div>
    );

    if (loading) {
        return <CustomLoading />;
    }

    return(
        <div className="mt-3 view-detail-card">
            <div className="view-detail-row">
                <div className="view-detail-label"><Building2 size={15}/> Policy Account</div>
                <div className="view-detail-value"><b>{data.name}</b></div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Building2 size={15}/> DBA</div>
                <div className="view-detail-value"><b>{data.dba}</b></div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Users size={15}/> Plan Admin</div>
                <div className="view-detail-value">{renderPlanAdmins()}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><StickyNote size={15}/> Notes</div>
                <div className="view-detail-value">{notes}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Mail size={15}/> Email History</div>
                <div className="view-detail-value">
                    <div className="dashboard-table-container">
                        <table className="table table-hover">
                            <thead>
                            <tr>
                                <th>Email Type</th>
                                <th>Date Sent</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td></td>
                                <td></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Settings size={15}/> Funding Email Frequency</div>
                <div className="view-detail-value">
                    {FundingEmailFrequency.find(f => f.value === data.fundingEmailFrequency)?.label || "N/A"}
                </div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> 2 Factor Authentication</div>
                <div className="view-detail-value">{data.twoFactorEnabled ? "Yes" : "No"}</div>
            </div>
            {data.isEA === "Yes" && (
            <div className="view-detail-row">
                <div className="view-detail-label"><Settings size={15}/> EA API Sync</div>
                <div className="view-detail-value">
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowSyncConfirm(true)}
                        disabled={syncingEa}
                    >
                        {syncingEa ? "Syncing..." : "Sync EA API"}
                    </button>
                </div>
            </div>
            )}
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Allow Dependent Edits by EEs</div>
                <div className="view-detail-value">{data.allowDependentEdit ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Wellness Instead of Lifestyle</div>
                <div className="view-detail-value">{data.enableWellness ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Has HSA</div>
                <div className="view-detail-value">{hasHsa ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Has LSA</div>
                <div className="view-detail-value">{hasLsa ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Has Flex</div>
                <div className="view-detail-value">{hasFlex ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Has Enhanced Benefits</div>
                <div className="view-detail-value">{hasAdditionalAccounts ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> PAD Enabled</div>
                <div className="view-detail-value">{data.enablePad ? "Yes" : "No"}</div>
            </div>
            <div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> PAD Completed</div>
                <div className="view-detail-value">{data.isPadComplete ? "Yes" : "No"}</div>
            </div>
            {data.hasCoveragePay ?
            (<div className="view-detail-row">
                <div className="view-detail-label"><Shield size={15}/> Has Coverage %</div>
                <div className="view-detail-value">Yes</div>
            </div>)
            : <></>}
            <div className="view-detail-row">
                <div className="view-detail-label"><FileText size={15}/> Enrollment Notes</div>
                <div className="view-detail-value">{data.enrollmentNotes}</div>
            </div>
            <Modal show={showSyncConfirm} style={{top:100}}>
                <Modal.Header>
                    <Modal.Title style={{fontSize:"17px"}}>Sync EA API</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sync this policy to the EA API feed?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSyncConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={confirmSyncEa}>
                        Sync
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
};

export default ViewPolicy;
